/**
 * Attestation section.
 *
 * Shows the canonical Authorization to Assess text loaded from the
 * backend at /api/v1/attestation/current. The buyer:
 *  1. Reads it (rendered verbatim — no rewording, the server's text
 *     is the legal anchor).
 *  2. Checks an affirmative box.
 *  3. Types their legal name to authorize.
 *
 * The typed name must match the contact name (case-insensitive,
 * whitespace-normalized) — the server enforces this at submission;
 * the inline match indicator is just an early visual cue.
 *
 * Loading / error states get explicit treatments. An error here
 * blocks Pay (the engagement can't proceed without a captured
 * attestation), so the retry button is prominent.
 */

import { useMemo } from "react";

import type {
  ReconConfiguratorAction,
  ReconConfiguratorState,
} from "../state";

import "./attestation-section.css";

interface Props {
  state: ReconConfiguratorState;
  dispatch: React.Dispatch<ReconConfiguratorAction>;
  /** Re-fetch the attestation text. The parent component owns the
   *  request lifecycle; this prop just triggers it. */
  onRetry: () => void | Promise<void>;
}

export function AttestationSection({ state, dispatch, onRetry }: Props) {
  const { attestation, attestedName, attestationChecked } = state;

  // Match check was removed — the buyer is signed in already, so the
  // "matches contact name" gate was theater. The legal binding is the
  // typed legal name + the checked box + the canonical text version
  // they saw (captured in attestation.text_hash on submission). What
  // we now show is "typed / blank" so the buyer knows the field has
  // accepted their input.
  const matchStatus = useMemo<"blank" | "typed">(() => {
    return attestedName.trim().length > 0 ? "typed" : "blank";
  }, [attestedName]);

  return (
    <section
      className="attestation-section"
      aria-labelledby="attestation-section-heading"
    >
      <header className="attestation-section-header">
        <div className="attestation-section-eyebrow">
          STEP 4 · AUTHORIZE THE SCAN
        </div>
        <h2 id="attestation-section-heading">
          You're telling us this is your asset to assess
        </h2>
        <p className="attestation-section-body">
          The scan is passive — we read what's already public — but we
          still need your authorization on file before we run it. Read,
          check, sign.
        </p>
      </header>

      {/* ---- Canonical text or its loading/error state ---- */}
      {attestation.kind === "loading" && (
        <div
          className="attestation-section-status attestation-section-status-loading"
          role="status"
        >
          Loading the authorization text…
        </div>
      )}
      {attestation.kind === "error" && (
        <div
          className="attestation-section-status attestation-section-status-error"
          role="alert"
        >
          <span>{attestation.message}</span>
          <button
            type="button"
            className="attestation-section-retry-btn"
            onClick={() => void onRetry()}
          >
            Reload
          </button>
        </div>
      )}
      {attestation.kind === "ok" && (
        <>
          <article
            className="attestation-section-text"
            aria-label="Authorization to Assess"
            tabIndex={0}
          >
            {attestation.data.text_body
              .split(/\n{2,}/)
              .map((para, i) => (
                <p key={i}>{para.trim()}</p>
              ))}
          </article>

          <div className="attestation-section-controls">
            <label className="attestation-section-checkbox-label">
              <input
                type="checkbox"
                className="attestation-section-checkbox"
                checked={attestationChecked}
                onChange={(e) =>
                  dispatch({
                    type: "set_attestation_checked",
                    checked: e.target.checked,
                  })
                }
              />
              <span>
                I'm authorized to commission this assessment on the
                listed domains and IPs.
              </span>
            </label>

            <div className="attestation-section-name-field">
              <label
                htmlFor="attestation-name"
                className="attestation-section-name-label"
              >
                Type your legal name to authorize
                <span
                  className="attestation-section-required"
                  aria-hidden="true"
                >
                  *
                </span>
              </label>
              <input
                id="attestation-name"
                type="text"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={attestedName}
                onChange={(e) =>
                  dispatch({
                    type: "set_attested_name",
                    value: e.target.value,
                  })
                }
                placeholder="Your full legal name"
                aria-describedby="attestation-name-status"
              />
              <MatchIndicator status={matchStatus} />
            </div>

            <p className="attestation-section-footnote">
              Version {attestation.data.version} of the Authorization to
              Assess.
            </p>
          </div>
        </>
      )}
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Live match indicator                                                        //
// --------------------------------------------------------------------------- //

interface MatchIndicatorProps {
  status: "blank" | "typed";
}

function MatchIndicator({ status }: MatchIndicatorProps) {
  if (status === "blank") {
    return (
      <p
        id="attestation-name-status"
        className="attestation-section-name-status attestation-section-name-status-blank"
      >
        Your typed name is the signature on this engagement's authorization.
      </p>
    );
  }
  return (
    <p
      id="attestation-name-status"
      className="attestation-section-name-status attestation-section-name-status-match"
    >
      Captured as your signature on this authorization.
    </p>
  );
}
